import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";

export default function UserCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const isEdit = id && id !== "new";

  const formik = useFormik({
    initialValues: {
      name: "",
      username: "",
      password: "",
      password_confirmation: "",
      isActive: "Active",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
      username: Yup.string().required("Username is required"),
      password: isEdit
        ? Yup.string().notRequired()
        : Yup.string()
            .min(6, "Password must be at least 6 characters")
            .required("Password is required"),
      password_confirmation: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .oneOf([Yup.ref("password")], "Passwords must match")
        .when("password", {
          is: (val) => Boolean(val),
          then: (schema) => schema.required("Confirm password is required"),
        }),
    }),
    onSubmit: async (values) => {
      const payload: any = {
        name: values.name,
        username: values.username,
        status: values.isActive === "Active",
      };
      if (values.password) {
        payload.password = values.password;
        payload.password_confirmation = values.password_confirmation;
      }

      try {
        let response;
        if (isEdit) {
          response = await request.put("users/" + id, payload);
        } else {
          response = await request.post("users/create", payload);
        }

        if (response?.data?.status) {
          toast.success(isEdit ? "User updated successfully!" : "User created successfully!");
          navigate("/admin/users");
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "An error occurred. Please try again.");
      }
    },
  });

  useEffect(() => {
    if (isEdit) {
      const fetchItem = async () => {
        setLoading(true);
        try {
          const response = await request.get(`users/${id}`);
          const item = response.data.data;
          formik.setValues({
            name: item.name || "",
            username: item.username || "",
            password: "",
            password_confirmation: "",
            isActive: item.status ? "Active" : "Inactive",
          });
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={isEdit ? "Edit User" : "Create New User"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/users"),
              }}
              primaryButton={{
                label: isEdit ? "Update User" : "Create User",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField name="name" label="Name" type="text" formik={formik} autoFocus />
                </Box>
                <Box className="form-field">
                  <TextField name="username" label="Username" type="text" formik={formik} />
                </Box>
              </Box>

              {!isEdit && (
                <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                  <Box className="form-field">
                    <TextField name="password" label="Password" type="password" formik={formik} />
                  </Box>
                  <Box className="form-field">
                    <TextField
                      name="password_confirmation"
                      label="Confirm Password"
                      type="password"
                      formik={formik}
                    />
                  </Box>
                </Box>
              )}

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}